import { type StoredStudyProgress, studyProgressFraction } from '@sigora/domain';

import { progressHeroLabel } from './launch-copy.ts';

/**
 * How recent a reading has to be for the card to stop qualifying it at all.
 *
 * Three server intervals and a little slack. Under this the reading is as current as the system
 * can make it and saying anything about its age would be noise.
 */
const FRESH_READING_MS = 30_000;

/**
 * How old a reading may be before the card stops presenting it as current at all.
 *
 * **Five minutes, not one, and the retry policy is why.** A poll that fails takes
 * `LIVE_PHASE_RETRY`, which backs off in minutes — so a chain that is alive and retrying through
 * a Remesh blip legitimately leaves gaps far longer than the ten-second cadence. Calling that
 * "stopped reporting" after a minute would be the card telling a confident lie in the other
 * direction, which is the same defect as the one this whole section exists to fix.
 *
 * Past this the card must stop drawing a live-looking picture over a frozen number: the count
 * stops moving while the elapsed clock does not, so a projection made from the two inflates the
 * longer the poller has been dead.
 */
const STALE_READING_MS = 5 * 60_000;

/** Minutes and hours, in words, for a span a researcher is watching rather than measuring. */
const durationLabel = (milliseconds: number): string => {
  const minutes = Math.floor(milliseconds / 60_000);
  if (minutes < 1) {
    return 'under a minute';
  }

  if (minutes < 60) {
    return minutes === 1 ? 'a minute' : `${String(minutes)} minutes`;
  }

  const hours = Math.round(minutes / 60);

  return hours === 1 ? 'about an hour' : `about ${String(hours)} hours`;
};

export interface ProgressCard {
  /**
   * How many have finished, short, for the hero over the bar: out of how many where there is a
   * target. Null where there is no count, which leaves `countLabel` to say why.
   */
  heroLabel: string | null;
  /**
   * The hero in a full sentence that names participants, for a screen reader, which hears this in
   * place of the hero's short form. Null exactly where `heroLabel` is.
   */
  heroSentence: string | null;
  /**
   * Why there is no count, in a sentence: no reading yet, or a study that does not report one.
   * Null exactly where there is a hero, which is then the card's one count line.
   */
  countLabel: string | null;
  /**
   * Whether a current reading has everyone the study asked for: a target above zero, a count at
   * or past it, and a reading that is not stale. The timing line's "everyone has finished" and the
   * Fielding step's closing line both follow it, so the two never disagree.
   */
  atTarget: boolean;
  /** The share of N that is done, as a percentage clamped for a bar. Null where there is none. */
  percent: number | null;
  /** How long it has been fielding, and how much longer it looks like taking. */
  timingLabel: string | null;
  /**
   * When the reading was taken, in words, or null where nothing has been read yet.
   *
   * Always shown rather than only when it is old. The number above it comes from the server's last
   * look at Remesh, not from this page, and a card that never says when it looked cannot be told
   * apart from one that is watching.
   */
  readingLabel: string | null;
  /** Whether that reading is old enough that the card has stopped presenting it as current. */
  stale: boolean;
}

/**
 * The live study's progress, in the words the card shows.
 *
 * Three things it is careful about, all of them ways a progress number lies.
 *
 * A **null count is not zero.** Completions are reported for flex studies and only for those, so
 * null means the study has no such concept — saying "0 of 8" about it would be a claim that
 * nobody has answered. It gets a sentence of its own.
 *
 * A count that **went down** is rendered as it arrived. A returned submission un-completes a
 * participant, so a smaller number than last time is Remesh's current answer rather than a bad
 * read, and this neither keeps the maximum nor treats the drop as an error.
 *
 * The **estimate is only ever an extrapolation of what has happened**, so it is offered only
 * once something has: with no completions there is no rate, and a "time remaining" invented
 * from N alone would be the panel's own guess wearing Remesh's authority.
 */
export const describeProgress = ({
  progress,
  launchedAt,
  now,
}: {
  progress: StoredStudyProgress | null;
  launchedAt: Date | null;
  now: Date;
}): ProgressCard => {
  if (progress === null) {
    return {
      heroLabel: null,
      heroSentence: null,
      countLabel: 'Waiting for the first reading from the study.',
      atTarget: false,
      percent: null,
      timingLabel: null,
      readingLabel: null,
      stale: false,
    };
  }

  const { completed_count: completed, target_completes: target } = progress;
  const fraction = studyProgressFraction(progress);

  const heroSentence =
    completed === null
      ? null
      : target === null
        ? `${String(completed)} participants have finished.`
        : `${String(completed)} of ${String(target)} participants have finished.`;

  const elapsedMs = launchedAt === null ? null : now.getTime() - launchedAt.getTime();
  const readingAgeMs = now.getTime() - new Date(progress.read_at).getTime();
  const stale = readingAgeMs >= STALE_READING_MS;
  const atTarget = !stale && fraction !== null && fraction >= 1;

  // No projection from a reading that has stopped moving. The count is frozen while the clock is
  // not, so the arithmetic below would report more and more time remaining the longer the poller
  // has been dead — a card that looks more alive the more broken it is.
  // Projected only while the study is short of a target above zero.
  const remainingMs =
    stale ||
    elapsedMs === null ||
    elapsedMs <= 0 ||
    completed === null ||
    completed <= 0 ||
    target === null ||
    fraction === null ||
    fraction >= 1
      ? null
      : (elapsedMs / completed) * (target - completed);

  const timingLabel =
    elapsedMs === null
      ? null
      : atTarget
        ? `Fielding for ${durationLabel(elapsedMs)}. Everyone it asked for has finished.`
        : remainingMs === null
          ? `Fielding for ${durationLabel(elapsedMs)}.`
          : `Fielding for ${durationLabel(elapsedMs)}, and about ${durationLabel(remainingMs)} to go at this rate.`;

  return {
    // Three bands, and each threshold is the constant rather than a re-typed copy of its value: a
    // literal equal to `STALE_READING_MS` here would make the middle band unreachable without
    // anything failing, since a collision like that makes code unreachable rather than wrong.
    readingLabel: stale
      ? `This reading is ${durationLabel(readingAgeMs)} old — the study has stopped reporting.`
      : readingAgeMs < FRESH_READING_MS
        ? 'Read just now.'
        : `Read ${durationLabel(readingAgeMs)} ago.`,
    stale,
    heroLabel: completed === null ? null : progressHeroLabel(completed, target),
    heroSentence,
    countLabel:
      completed === null ? 'This study does not report how many participants have finished.' : null,
    atTarget,
    // Clamped for the bar and for the bar only — the count above it is the raw number. An
    // over-recruited study really can pass its N, and a bar wider than its track is a rendering
    // bug rather than an honest one.
    percent: fraction === null ? null : Math.min(Math.max(fraction, 0), 1) * 100,
    timingLabel,
  };
};
